import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { format, parseISO } from "date-fns";
import { CalendarIcon, ChevronDown, Megaphone, Pencil, Plus, X } from "lucide-react";
import { AppLayout, PageBackLink } from "../../components/layout";
import { AlertBanner, ContentCard, DataTable, EmptyState, LoadingState, PageHeader, SearchInput, ui } from "../../components/ui";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { TablePagination } from "../../components/ui/TablePagination";
import { cn } from "../../lib/utils";
import { LeaveActionToast } from "../leave-management/components/LeaveActionToast";
import {
  addTenantAnnouncement,
  getActiveTenants,
  getTenantAnnouncement,
  searchTenantAnnouncements,
  updateTenantAnnouncement,
} from "../../services/tenantAnnouncementService";

const PAGE_SIZE = 10;
const TITLE_MAX = 100;
const EMPTY_FORM = { announcementTitle: "", announcementDescription: "", startDate: "", endDate: "" };
const fieldLabelClass = "block text-xs font-semibold per-file-hint mb-1.5";
const inputClass = "w-full h-10 px-3 rounded-xl border bg-white text-sm outline-hidden focus:ring-1 focus:ring-accent";
const iconBtnClass = "h-8 w-8 p-0 rounded-lg border-(--color-text-border)";

const formatDate = (value) => (value ? format(parseISO(value), "PPP") : "—");

function DateField({ label, value, error, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex-1">
      <label className={fieldLabelClass}>{label} <span className="text-error">*</span></label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn(
            "w-full h-10 justify-start text-left font-normal rounded-xl border bg-white px-3 text-sm hover:bg-workspace/40",
            !value && "text-muted-foreground",
            error ? "border-error" : "border-(--color-text-border)"
          )}>
            <CalendarIcon className="mr-2 h-4 w-4 per-file-hint" />
            {value ? formatDate(value) : <span>{t("dateTime.pickADate")}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
          <Calendar mode="single" selected={value ? parseISO(value) : undefined} initialFocus
            onSelect={(date) => date && onChange(format(date, "yyyy-MM-dd"))} className="p-3 pointer-events-auto" />
        </PopoverContent>
      </Popover>
      {error ? <p className="text-[11px] text-error mt-1">{error}</p> : null}
    </div>
  );
}

function TenantMultiSelect({ label, tenants, value, error, onChange }) {
  const { t } = useTranslation();
  const selectedNames = tenants.filter((tenant) => value.includes(tenant.tenantId)).map((tenant) => tenant.tenantName);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div>
      <label className={fieldLabelClass}>{label} <span className="text-error">*</span></label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn(
            "w-full h-10 justify-between text-left font-normal rounded-xl border bg-white px-3 text-sm hover:bg-workspace/40",
            !selectedNames.length && "text-muted-foreground",
            error ? "border-error" : "border-(--color-text-border)"
          )}>
            <span className="truncate">{selectedNames.length ? selectedNames.join(", ") : t("tenantAnnouncements.selectTenants")}</span>
            <ChevronDown className="h-4 w-4 per-file-hint" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-2.5 rounded-xl border-(--color-text-border) bg-white">
          <div className="flex flex-col gap-1.5 max-h-[200px] overflow-y-auto">
            {tenants.length === 0 ? (
              <p className="px-1 py-1 text-sm per-file-hint">{t("tenantAnnouncements.noTenants")}</p>
            ) : tenants.map((tenant) => (
              <label key={tenant.tenantId} className="flex items-center gap-2 text-sm text-foreground cursor-pointer px-1 py-1 rounded-lg hover:bg-workspace/40">
                <input type="checkbox" className="cursor-pointer" checked={value.includes(tenant.tenantId)}
                  onChange={() => toggle(tenant.tenantId)} />
                {tenant.tenantName}
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {error ? <p className="text-[11px] text-error mt-1">{error}</p> : null}
    </div>
  );
}

/** `announcement` is the detail response when editing, null when adding. */
function AnnouncementDialog({ open, announcement, onClose, onSaved, onRefresh }) {
  const { t } = useTranslation();
  const isEdit = Boolean(announcement);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantIds, setTenantIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(announcement ? Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, announcement[k] ?? ""])) : EMPTY_FORM);
    setTenantIds([]);
    setErrors({});
    setBanner("");
    if (announcement) return;
    getActiveTenants()
      .then(setTenants)
      .catch((err) => setBanner(err?.message || t("tenantAnnouncements.tenantsLoadFailed")));
  }, [open, announcement, t]);

  const labels = {
    announcementTitle: t("common.title"),
    announcementDescription: t("common.description2"),
    startDate: t("dateTime.startDate"),
    endDate: t("dateTime.endDate"),
  };

  function update(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    const next = {};
    Object.keys(EMPTY_FORM).forEach((key) => {
      if (!String(form[key]).trim()) next[key] = t("validation.fieldRequired", { field: labels[key] });
    });
    if (!next.announcementTitle && form.announcementTitle.trim().length > TITLE_MAX) {
      next.announcementTitle = t("tenantAnnouncements.titleTooLong", { max: TITLE_MAX });
    }
    if (!isEdit && tenantIds.length === 0) {
      next.tenantIds = t("validation.fieldRequired", { field: t("tenantAnnouncements.tenants") });
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    const fields = {
      announcementTitle: form.announcementTitle.trim(),
      announcementDescription: form.announcementDescription.trim(),
      startDate: form.startDate,
      endDate: form.endDate,
    };
    setSubmitting(true);
    setBanner("");
    try {
      if (isEdit) {
        const { announcementId, tenantId } = announcement;
        await updateTenantAnnouncement(announcementId, { announcementId, tenantId, ...fields });
        onSaved(true);
        return;
      }
      // The add API takes one tenantId, so each selected tenant gets its own request.
      const results = await Promise.allSettled(tenantIds.map((tenantId) => addTenantAnnouncement({ tenantId, ...fields })));
      const failedIds = tenantIds.filter((_, i) => results[i].status === "rejected");
      if (!failedIds.length) {
        onSaved(false);
        return;
      }
      if (failedIds.length < tenantIds.length) onRefresh();
      setTenantIds(failedIds);
      const names = tenants.filter((tenant) => failedIds.includes(tenant.tenantId)).map((tenant) => tenant.tenantName).join(", ");
      const reason = results.find((r) => r.status === "rejected").reason?.message;
      setBanner([t("tenantAnnouncements.createFailedFor", { tenants: names }), reason].filter(Boolean).join("\n"));
    } catch (err) {
      setBanner(err?.message || t("tenantAnnouncements.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content aria-describedby={undefined} className={cn(
          "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
          "w-[95vw] sm:w-[570px] max-w-[570px] max-h-[90vh] overflow-hidden flex flex-col",
          "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
          "font-sans duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        )}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70 shrink-0">
            <DialogTitle className="text-[18px] font-bold text-foreground tracking-tight">
              {t(isEdit ? "tenantAnnouncements.editAnnouncement" : "tenantAnnouncements.newAnnouncement")}
            </DialogTitle>
            <button type="button" onClick={onClose} disabled={submitting} aria-label={t("formActions.close")}
              className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-workspace">
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={handleSubmit} aria-busy={submitting} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {banner ? <div role="alert" className="bg-error/10 border border-error/25 rounded-xl px-4 py-3 text-xs text-error whitespace-pre-line">{banner}</div> : null}

            {!isEdit ? (
              <TenantMultiSelect label={t("tenantAnnouncements.tenants")} tenants={tenants} value={tenantIds} error={errors.tenantIds}
                onChange={(ids) => { setTenantIds(ids); setErrors((prev) => ({ ...prev, tenantIds: undefined })); }} />
            ) : null}

            <div>
              <label className={fieldLabelClass}>{labels.announcementTitle} <span className="text-error">*</span></label>
              <input value={form.announcementTitle} onChange={(e) => update("announcementTitle", e.target.value)}
                className={cn(inputClass, errors.announcementTitle ? "border-error" : "border-(--color-text-border)")} />
              {errors.announcementTitle ? <p className="text-[11px] text-error mt-1">{errors.announcementTitle}</p> : null}
            </div>

            <div>
              <label className={fieldLabelClass}>{labels.announcementDescription} <span className="text-error">*</span></label>
              <Textarea rows={5} value={form.announcementDescription} onChange={(e) => update("announcementDescription", e.target.value)}
                className={cn("rounded-xl bg-white text-sm", errors.announcementDescription ? "border-error" : "border-(--color-text-border)")} />
              {errors.announcementDescription ? <p className="text-[11px] text-error mt-1">{errors.announcementDescription}</p> : null}
            </div>

            <div className="flex gap-3">
              <DateField label={labels.startDate} value={form.startDate} error={errors.startDate} onChange={(v) => update("startDate", v)} />
              <DateField label={labels.endDate} value={form.endDate} error={errors.endDate} onChange={(v) => update("endDate", v)} />
            </div>

            <div className="pt-4 border-t border-(--color-text-border)/70 flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={submitting}
                className="h-11 px-6 rounded-xl border-primary/30 text-primary font-semibold text-sm hover:bg-workspace/40 bg-white">
                {t("formActions.cancel")}
              </Button>
              <Button type="submit" disabled={submitting}
                className="h-11 px-6 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-accent transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] min-w-[120px]">
                {submitting ? t("tenantAnnouncements.saving") : t(isEdit ? "formActions.save" : "formActions.create")}
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export function TenantAnnouncementsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState({ open: false, announcement: null });
  const [openingId, setOpeningId] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const result = await searchTenantAnnouncements({ title, page: page - 1, size: PAGE_SIZE });
      if (request !== requestRef.current) return;
      setRows(result.content);
      setTotalItems(result.totalElements);
      setTotalPages(Math.max(1, result.totalPages));
    } catch (err) {
      if (request !== requestRef.current) return;
      setRows([]);
      setError(err?.message || t("tenantAnnouncements.loadFailed"));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [title, page, t]);

  useEffect(() => { load(); }, [load]);

  async function openEdit(id) {
    setOpeningId(id);
    try {
      setDialog({ open: true, announcement: await getTenantAnnouncement(id) });
    } catch (err) {
      setToast({ type: "error", message: err?.message || t("tenantAnnouncements.detailFailed") });
    } finally {
      setOpeningId(null);
    }
  }

  function handleSaved(isEdit) {
    setDialog({ open: false, announcement: null });
    setToast({ type: "success", message: t(isEdit ? "tenantAnnouncements.updated" : "tenantAnnouncements.created") });
    load();
  }

  const editButton = (row) => (
    <Button variant="outline" className={iconBtnClass} disabled={openingId === row.announcementId}
      onClick={() => openEdit(row.announcementId)} aria-label={t("common.edit")}>
      <Pencil className="h-3.5 w-3.5 text-primary" />
    </Button>
  );

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <PageHeader back={<PageBackLink to="/" />}
        title={t("tenantAnnouncements.pageTitle")}
        action={
          <Button onClick={() => setDialog({ open: true, announcement: null })} className={ui.primaryBtn}>
            <Plus className="h-4 w-4" />
            {t("tenantAnnouncements.newAnnouncement")}
          </Button>
        } />

      <div className="my-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <SearchInput submitOnEnter value={title} placeholder={t("tenantAnnouncements.searchByTitle")}
          onChange={(e) => { setPage(1); setTitle(e.target.value.trim()); }} />
      </div>

      {error ? <AlertBanner type="error">{error}</AlertBanner> : null}

      <ContentCard>
        {loading ? (
          <LoadingState message={t("common.loading")} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Megaphone} title={t("tenantAnnouncements.noAnnouncements")}
            message={t(title ? "tenantAnnouncements.noMatch" : "tenantAnnouncements.getStarted")} />
        ) : (
          <>
            <DataTable minWidth="800px" columns={[
              { key: "tenantId", label: t("common.tenantId") },
              { key: "title", label: t("common.title") },
              { key: "startDate", label: t("dateTime.startDate") },
              { key: "endDate", label: t("dateTime.endDate") },
              { key: "actions", label: t("common.actions"), className: "text-right" },
            ]}>
              {rows.map((row) => (
                <tr key={row.announcementId} className="hover:bg-workspace/20 transition-colors">
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm text-foreground">{row.tenantId ?? "—"}</td>
                  <td className="px-4 py-3.5">
                    <p className="font-semibold text-foreground text-sm truncate max-w-[380px]">{row.announcementTitle || "—"}</p>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm text-foreground">{formatDate(row.startDate)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap text-sm text-foreground">{formatDate(row.endDate)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">{editButton(row)}</div>
                  </td>
                </tr>
              ))}
            </DataTable>

            <div className="md:hidden space-y-4 p-4">
              {rows.map((row) => (
                <div key={row.announcementId}
                  className="p-4 rounded-xl border border-(--color-text-border)/50 bg-white shadow-xs flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground text-sm truncate">{row.announcementTitle || "—"}</p>
                    <p className="mt-1 text-xs per-file-hint">{t("common.tenantId")}: {row.tenantId ?? "—"}</p>
                    <p className="mt-1 text-xs per-file-hint">{formatDate(row.startDate)} – {formatDate(row.endDate)}</p>
                  </div>
                  {editButton(row)}
                </div>
              ))}
            </div>

            <TablePagination page={page} totalPages={totalPages} totalItems={totalItems}
              pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </ContentCard>

      <AnnouncementDialog open={dialog.open} announcement={dialog.announcement}
        onClose={() => setDialog({ open: false, announcement: null })} onSaved={handleSaved} onRefresh={load} />
    </AppLayout>
  );
}
